import { 
    UserProfile, 
    LoginResponse, 
    RegisterRequest, 
    RegisterResponse,
    ForgotPasswordRequest,
    ForgotPasswordResponse,
    ResetPasswordRequest,
    VerifyEmailResponse,
    UpdateProfileRequest,
    UpdateProfileResponse
} from 'types/User';
import { apiGet, apiPost, apiPut, apiPutFormData } from 'utils/apiClient';

// -----------------------------------------------------------------------------
//   Service calls
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
async function getUserProfile(userId: string, token: string): Promise<UserProfile | null> {
    try {
        return await apiGet<UserProfile>(`/users/profile/${userId}`, token);
    } catch {
        return null;
    }
}

// -----------------------------------------------------------------------------
async function login(identifier: string, password: string): Promise<LoginResponse>
{   
    if (process.env.NODE_ENV !== 'production') {
        console.log(process.env);
    }

    return apiPost<LoginResponse>(
        `/users/authentication/login`,
        { identifier, password }
    );
}

// -----------------------------------------------------------------------------
async function register(registerObject: RegisterRequest): Promise<string>
{  
    const data = await apiPost<RegisterResponse>(
        `/users/authentication`,
        { registerObject }
    );
    
    if (process.env.NODE_ENV !== 'production') {
        console.log(data);
    }
    
    return data.message || '';
}

// -----------------------------------------------------------------------------
async function userForgotPassword(userData: ForgotPasswordRequest, userId?: string, token?: string): Promise<ForgotPasswordResponse> {
    const result = await apiPost<ForgotPasswordResponse>(
        `/users/authentication/forgot-password`,
        { userData },
        token
    );
    return { message: result.message || '' };
}

// -----------------------------------------------------------------------------
async function resetPasswordWithToken(payload: ResetPasswordRequest): Promise<ForgotPasswordResponse> {
    const result = await apiPost<ForgotPasswordResponse>(
        `/users/authentication/reset-password`,
        payload
    );
    return { message: result.message || '' };
}

// -----------------------------------------------------------------------------
async function verifyEmailToken(token: string): Promise<VerifyEmailResponse> {
    const encoded = encodeURIComponent(token);
    const result = await apiGet<VerifyEmailResponse>(`/users/authentication/verify-email?token=${encoded}`);
    return { message: result.message || '' };
}

// -----------------------------------------------------------------------------
async function resendVerificationEmail(identifier: string): Promise<VerifyEmailResponse> {
    const result = await apiPost<VerifyEmailResponse>(
        `/users/authentication/resend-verification`,
        { identifier }
    );
    return { message: result.message || '' };
}

// -----------------------------------------------------------------------------
async function updateProfile(
    profileChanges: UpdateProfileRequest, 
    userId: string, 
    token: string, 
    profilePictureFile?: File
): Promise<UpdateProfileResponse> {
    let output: UpdateProfileResponse = { message: '' };

    if (profilePictureFile) {
        // Use FormData for file uploads
        const formData = new FormData();
        formData.append('profileChanges', JSON.stringify(profileChanges));
        formData.append('profilePicture', profilePictureFile);
        
        const result = await apiPutFormData<UpdateProfileResponse>(
            `/users/profile/${userId}`,
            formData,
            token
        );
        output = result;
    } else {
        // Use JSON for regular updates
        const result = await apiPut<UpdateProfileResponse>(
            `/users/profile/${userId}`,
            { profileChanges },
            token
        );
        output = result;
    }
    
    return output;
}

async function updateOnboardingCompleted(
    userId: string,
    token: string,
    completed: boolean
): Promise<UpdateProfileResponse> {
    return updateProfile({ onboardingCompleted: completed }, userId, token);
}
// -----------------------------------------------------------------------------
//   Helpers
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
function userLogged(userId?: string, token?: string)
{
    return userId !== undefined && token !== undefined;
}

// -----------------------------------------------------------------------------
function getUserId(userId?: string)
{
    return userId;
}

// -----------------------------------------------------------------------------
function setUserId(id: string, userId?: string)
{
    userId = id;
}

// -----------------------------------------------------------------------------
function getToken(token?: string)
{
    return token;
}

// -----------------------------------------------------------------------------
function setToken(newToken: string, token?: string)
{
    token = newToken;
}

// -----------------------------------------------------------------------------
async function demoLogin(language: string): Promise<LoginResponse> {
    return apiPost<LoginResponse>('/users/demo/create', { language });
}

export { 
    updateProfile,
    updateOnboardingCompleted,
    userForgotPassword,
    resetPasswordWithToken,
    verifyEmailToken,
    resendVerificationEmail,
    setUserId,
    setToken,
    getToken, 
    register, 
    getUserId, 
    getUserProfile, 
    userLogged,
    login,
    demoLogin,
};
